/** leads/LeadProposal.tsx — 리드 포털 경정청구 제안서 셸 (A4 2쪽, 본문은 ProposalRefund)
 *  관리 DB 카드에서 열린다. 화면: 데스크 배경 위 A4 카드 2장(모바일은 폭에 맞춰 축소) · 인쇄: 페이지당 1장, 여백 0.
 *  인증 만료 DB(벤처·메인비즈·이노비즈)는 이 화면이 아니라 기존 만료 안내 DM(…/leads/:id/dm)을 연다 — 사용자 지시 2026-09-19.
 *  2026-09-19: ① 본문을 컴포넌트로 분리 ② 「PDF 저장」 — 서버 Chromium 이 이 화면을 인쇄 모드(?proposal=…&print=1)로 열어 PDF 로 만든다.
 *     모바일(삼성 인터넷 · 카카오톡 인앱 등)은 window.print() 가 먹지 않아 저장·공유가 불가능했던 문제의 해법. 터치 기기는 공유 시트(navigator.share)로 바로 카카오톡 전송도 된다.
 *  ③ 모바일에서 A4 가 오른쪽으로 밀려 잘리던 문제 — 축소 기준점(transform-origin)을 좁은 화면에선 왼쪽 위로 (leads.css). */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { portalProposal, portalProposalPdf } from './api';
import type { ProposalData } from './types';
import ProposalRefund from './ProposalRefund';

interface Props { token: string; leadId: string; corpName: string; onBack: () => void; print?: boolean; incomesInit?: string }

const isTouch = () => typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: none) and (pointer: coarse)').matches;
const inApp = () => /KAKAOTALK|Instagram|FB_IAB|FBAN|NAVER|Line\//i.test(navigator.userAgent || '');
/** 'a,b,c' → [a,b,c] (만원). 값이 셋 다 양수일 때만 채택 */
function parseIncomes(s?: string): [number, number, number] | null {
  const n = String(s || '').split(',').map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0);
  return n.length === 3 ? [n[0], n[1], n[2]] : null;
}

export default function LeadProposal({ token, leadId, corpName, onBack, print, incomesInit }: Props) {
  const [d, setD] = useState<ProposalData | null>(null);
  const [err, setErr] = useState('');
  const [incomes, setIncomes] = useState<[number, number, number]>(() => parseIncomes(incomesInit) || [5000, 10000, 15000]); // Case1·2·3 (만원) — Case2 = 과표 1억 = 카드 환급 예상과 같은 기준
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [toast, setToast] = useState('');
  const toastT = useRef(0);
  const say = (m: string, ms = 3000) => { setToast(m); window.clearTimeout(toastT.current); toastT.current = window.setTimeout(() => setToast(''), ms); };

  useEffect(() => {
    portalProposal(token, leadId).then(setD).catch((e) => setErr((e as Error).message || '제안서를 불러오지 못했습니다'));
  }, [token, leadId]);

  // 화면: 폭에 맞춰 축소 (A4 794px 기준). 인쇄·PDF 모드에서는 CSS 가 scale 을 무시한다
  useLayoutEffect(() => {
    if (print) { setScale(1); return undefined; }
    const fit = () => { const w = wrapRef.current ? wrapRef.current.clientWidth : 0; setScale(w > 0 && w < 794 + 32 ? Math.max(0.3, (w - 24) / 794) : 1); };
    fit(); window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [d, print]); // 데이터 도착 후(본문 렌더 뒤) 한 번 더 — 로딩 화면에는 wrapRef가 없다

  useEffect(() => { document.body.classList.add('lpp-print-body'); return () => { document.body.classList.remove('lpp-print-body'); document.body.removeAttribute('data-proposal-ready'); }; }, []);

  // A4 한 쪽(1123px)은 overflow:hidden — 넘치면 «말없이 잘린다». 투자 건수가 많거나 글자가 길어
  // 본문이 넘칠 때만 그 쪽 본문을 아주 조금 줄여 잘림을 막는다 (보통은 1 = 아무 것도 하지 않음, 2026-09-12)
  const bodyRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [fit, setFit] = useState<[number, number]>([1, 1]);
  useLayoutEffect(() => {
    if (!d) return undefined;
    let alive = true;
    // transform 은 레이아웃에 영향이 없으므로(=scrollHeight 불변) 스케일이 적용된 상태에서 재측정해도 값이 안정적이다
    const measure = () => {
      const next: [number, number] = [0, 1].map((i) => {
        const el = bodyRefs.current[i];
        if (!el) return 1;
        const sh = el.scrollHeight; const ch = el.clientHeight;
        return sh > ch + 1 ? Math.max(0.72, Math.floor((ch / sh) * 1000) / 1000) : 1;
      }) as [number, number];
      if (alive) setFit((prev) => (prev[0] === next[0] && prev[1] === next[1] ? prev : next));
    };
    measure();
    const t = window.setTimeout(() => { measure(); if (print && alive) document.body.setAttribute('data-proposal-ready', '1'); }, 350); // 웹폰트 로드 후 한 번 더 → PDF 렌더러에 「준비 끝」 신호
    return () => { alive = false; window.clearTimeout(t); };
  }, [d, incomes, scale, print]);
  const bodyStyle = (i: number): React.CSSProperties => (fit[i] < 1 ? { transform: 'scale(' + fit[i] + ')', transformOrigin: 'top center' } : {});
  const bodyRef = (i: number) => (el: HTMLDivElement | null) => { bodyRefs.current[i] = el; };

  const touchDevice = isTouch() || (navigator.maxTouchPoints || 0) > 0; // 터치 기기는 「인쇄」(window.print) 숨김 — 삼성 인터넷·인앱 브라우저에서 동작하지 않음
  const title = (corpName || (d ? d.client_name : '')) + ' · 경정청구 제안서';

  /** PDF 저장·공유 — 서버가 이 화면을 인쇄 모드로 열어 PDF 로 만든다. 터치 기기는 공유 시트(카카오톡 등) 우선, 아니면 다운로드 */
  const savePdf = async () => {
    if (pdfBusy || !d) return;
    setPdfBusy(true);
    say('PDF를 만드는 중입니다 — 5~10초 걸립니다', 12000);
    try {
      const { blob, filename } = await portalProposalPdf(token, leadId, incomes.join(','));
      const file = new File([blob], filename, { type: 'application/pdf' });
      const nav = navigator as Navigator & { canShare?: (data: { files: File[] }) => boolean; share?: (data: { files: File[]; title?: string }) => Promise<void> };
      if (isTouch() && typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files: [file] })) {
        try { await nav.share({ files: [file], title: filename }); setToast(''); return; }
        catch (e) { if ((e as Error).name === 'AbortError') { setToast(''); return; } /* 공유 미지원 → 다운로드로 */ }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); a.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      say(inApp() ? '저장이 안 보이면 우측 상단 ⋯ → 「다른 브라우저로 열기」(Chrome·Safari)에서 다시 눌러주세요' : 'PDF 저장 완료 — 다운로드 폴더를 확인하세요', 6000);
    } catch (e) { say((e as Error).message || 'PDF를 만들지 못했습니다', 5000); }
    finally { setPdfBusy(false); }
  };

  if (err) return <div className="lpp-root"><div className="lpp-msg">{err}<br /><button className="lpp-back" onClick={onBack}>‹ 돌아가기</button></div></div>;
  if (!d) return <div className={'lpp-root' + (print ? ' print' : '')}><div className="lpp-msg">제안서를 준비하는 중…</div></div>;

  return (
    <div className={'lpp-root' + (print ? ' print' : '')} ref={wrapRef}>
      {!print && (
        <div className="lpp-bar noprint">
          <button className="lpp-back" onClick={onBack} aria-label="뒤로">‹ 관리 DB</button>
          <span className="lpp-bartitle">{title}</span>
          {!touchDevice && <button className="lpp-print pc" onClick={() => window.print()} title="브라우저 인쇄 (PC)">인쇄</button>}
          <button className="lpp-pdf" onClick={savePdf} disabled={pdfBusy} aria-busy={pdfBusy}>{pdfBusy ? 'PDF 만드는 중…' : 'PDF 저장'}</button>
        </div>
      )}
      <div className="lpp-desk" style={{ height: scale < 1 ? (1123 * 2 + 24) * scale + 40 : undefined }}>
        <div className="lpp-pages" style={{ transform: scale < 1 ? 'scale(' + scale + ')' : undefined }}>
          <ProposalRefund d={d} incomes={incomes} setIncomes={setIncomes} bodyRef={bodyRef} bodyStyle={bodyStyle} />
        </div>
      </div>
      {toast && <div className="lpp-toast noprint" role="status">{toast}</div>}
    </div>
  );
}
